/* ==========================================================================
   Buena Vita — cart behaviour

   Quantity edits go through /cart/change.js and patch the DOM in place: the
   line total, the subtotal, the header count and the free-shipping meter all
   move without a reload. The one case that does reload is emptying the cart,
   because the empty state is a different server-rendered layout and faking it
   client-side would only drift.

   The cross-sell is filtered here rather than in Liquid on purpose. What is in
   the cart changes as the shopper edits it, and a server-rendered exclusion
   list would start recommending something they had just removed — or keep
   hiding something they no longer own.

   Depends on window.BVFX for money formatting, the shared cart read, the
   meter and toasts.
   ========================================================================== */

(function () {
  'use strict';

  var KEY = 'bv:routine';

  function money(cents) {
    if (window.BVFX && window.BVFX.money) return window.BVFX.money(cents);
    return '$' + (cents / 100).toFixed(2);
  }

  function routine() {
    try {
      var v = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Object.prototype.toString.call(v) === '[object Array]' ? v : [];
    } catch (err) {
      return [];
    }
  }

  function toast(opts) {
    if (window.BVFX && window.BVFX.toast) window.BVFX.toast(opts);
  }

  function init(section) {
    if (section.dataset.bvCartReady) return;
    section.dataset.bvCartReady = '1';

    var lines = section.querySelector('[data-bv-lines]');
    var subtotalEl = section.querySelector('[data-bv-subtotal]');
    var headCount = section.querySelector('[data-bv-cart-headcount]');

    /* ---------- cross-sell ---------- */

    var cross = section.querySelector('[data-bv-cross]');
    var crossTitle = section.querySelector('[data-bv-cross-title]');

    function inCart() {
      var out = {};
      [].slice.call(section.querySelectorAll('[data-bv-line]')).forEach(function (li) {
        var name = li.querySelector('.bv-cart__line-name');
        var href = name ? name.getAttribute('href') || '' : '';
        var m = href.match(/\/products\/([^/?#]+)/);
        if (m) out[m[1]] = true;
      });
      return out;
    }

    function paintCross() {
      if (!cross) return;

      var have = inCart();
      var want = routine();
      var list = cross.querySelector('[data-bv-cross-list]');
      var items = [].slice.call(cross.querySelectorAll('li[data-handle]'));
      var shownFromRoutine = 0;
      var shown = 0;

      /* Anything they put in their routine and did not buy goes first, and
         changes the heading — that is a reminder, not a recommendation. */
      items.sort(function (a, b) {
        var ai = want.indexOf(a.dataset.handle);
        var bi = want.indexOf(b.dataset.handle);
        if (ai === bi) return 0;
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      });

      items.forEach(function (li) {
        list.appendChild(li);
        var hide = !!have[li.dataset.handle] || shown >= 4;
        li.hidden = hide;
        if (!hide) {
          shown++;
          if (want.indexOf(li.dataset.handle) !== -1) shownFromRoutine++;
        }
      });

      cross.hidden = shown === 0;

      if (crossTitle) {
        crossTitle.textContent = shownFromRoutine
          ? crossTitle.dataset.routineText || 'Still in your routine'
          : crossTitle.dataset.plainText || 'Goes well with this';
      }
    }

    if (crossTitle) {
      crossTitle.dataset.routineText = crossTitle.textContent.trim();
      crossTitle.dataset.plainText = 'Goes well with this';
    }
    paintCross();

    /* ---------- quantity ---------- */

    var busy = false;

    function setBusy(li, on) {
      if (on) li.setAttribute('data-busy', ''); else li.removeAttribute('data-busy');
      [].slice.call(li.querySelectorAll('button, input')).forEach(function (el) { el.disabled = on; });
    }

    /* v19 (2026-10-11): Shopify gives a line a NEW key whenever its quantity
       changes while an automatic discount is running (Bundle & save 15% is), and
       the other lines' keys move with the reallocated discount too. Matching on
       the old key made every +/- look like a removal: the row vanished from the
       list while the cart still held the item, and the other rows kept dead keys,
       so their buttons failed next. After each change every row is matched again,
       first on its key, then on its variant among items no other row has
       claimed, and its key, quantity and line total are refreshed. */
    function applyItem(row, item) {
      row.dataset.key = item.key;
      var total = row.querySelector('[data-bv-line-total]');
      if (total) total.textContent = money(item.final_line_price);
      var qtyIn = row.querySelector('[data-bv-qty-in]');
      if (qtyIn) qtyIn.value = item.quantity;
      /* The bundle label under the name moves with the quantity too (on at 3+). */
      var allocs = item.line_level_discount_allocations || [];
      var list = row.querySelector('.bv-cart__line-discounts');
      if (!allocs.length) { if (list) list.parentNode.removeChild(list); return; }
      if (!list) {
        var unit = row.querySelector('.bv-cart__line-unit');
        if (!unit) return;
        list = document.createElement('ul');
        list.className = 'bv-cart__line-discounts';
        unit.parentNode.insertBefore(list, unit.nextSibling);
      }
      list.textContent = '';
      allocs.forEach(function (a) {
        var liEl = document.createElement('li');
        liEl.textContent = ((a.discount_application && a.discount_application.title) || 'Discount') + ' (−' + money(a.amount) + ')';
        list.appendChild(liEl);
      });
    }

    function rekey(cart, target, targetKey, removing) {
      var rows = [].slice.call(section.querySelectorAll('[data-bv-line]')).filter(function (row) {
        return !(removing && row === target);
      });
      var keyOf = function (row) { return row === target ? targetKey : row.dataset.key; };
      var claimed = {};
      var found = null;
      var pending = [];
      rows.forEach(function (row) {
        var hit = null;
        for (var i = 0; i < cart.items.length; i++) {
          if (cart.items[i].key === keyOf(row) && !claimed[cart.items[i].key]) { hit = cart.items[i]; break; }
        }
        if (hit) {
          claimed[hit.key] = 1;
          applyItem(row, hit);
          if (row === target) found = hit;
        } else {
          pending.push(row);
        }
      });
      pending.forEach(function (row) {
        var variant = String(keyOf(row)).split(':')[0];
        for (var i = 0; i < cart.items.length; i++) {
          var it = cart.items[i];
          if (!claimed[it.key] && String(it.variant_id) === variant) {
            claimed[it.key] = 1;
            applyItem(row, it);
            if (row === target) found = it;
            break;
          }
        }
      });
      return found;
    }

    function change(li, qty) {
      if (busy) return;
      var key = li.dataset.key;
      var input = li.querySelector('[data-bv-qty-in]');
      var previous = parseInt(input.value, 10) || 1;

      qty = Math.max(0, qty);
      busy = true;
      setBusy(li, true);

      fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      })
        .then(function (r) {
          return r.text().then(function (raw) {
            var data = null;
            try { data = JSON.parse(raw); } catch (err) { data = null; }
            return { ok: r.ok, data: data };
          });
        })
        .then(function (res) {
          busy = false;

          if (!res.ok || !res.data) {
            setBusy(li, false);
            input.value = previous;
            toast({
              title: 'Could not update that',
              sub: res.data ? (res.data.description || res.data.message || '') : '',
              error: true
            });
            return;
          }

          var cart = res.data;

          if (!cart.item_count) {
            window.location.reload();
            return;
          }

          var still = rekey(cart, li, key, qty === 0);

          if (!still) {
            li.setAttribute('data-going', '');
            var gone = false;
            var drop = function () {
              if (gone) return;
              gone = true;
              li.parentNode && li.parentNode.removeChild(li);
              paintCross();
            };
            li.addEventListener('transitionend', drop, { once: true });
            /* transitionend never arrives under prefers-reduced-motion, where
               the transition is none. */
            setTimeout(drop, 400);
          } else {
            setBusy(li, false);
            input.value = still.quantity;
            var total = li.querySelector('[data-bv-line-total]');
            if (total) total.textContent = money(still.final_line_price);
          }

          if (subtotalEl) subtotalEl.textContent = money(cart.total_price);
          if (headCount) {
            headCount.textContent = cart.item_count + (cart.item_count === 1 ? ' item' : ' items');
          }

          if (window.BVFX && window.BVFX.refresh) {
            window.BVFX.refresh().then(function () {
              if (window.BVFX.refreshMeter) window.BVFX.refreshMeter(section);
            });
          }
        })
        .catch(function () {
          busy = false;
          setBusy(li, false);
          input.value = previous;
          toast({ title: 'Network problem', sub: 'Check your connection and try again.', error: true });
        });
    }

    if (lines) {
      lines.addEventListener('click', function (e) {
        var step = e.target.closest('[data-bv-step]');
        if (step) {
          var li = step.closest('[data-bv-line]');
          var input = li.querySelector('[data-bv-qty-in]');
          change(li, (parseInt(input.value, 10) || 0) + parseInt(step.dataset.bvStep, 10));
          return;
        }

        var rm = e.target.closest('[data-bv-remove]');
        if (rm) {
          change(rm.closest('[data-bv-line]'), 0);
        }
      });

      lines.addEventListener('change', function (e) {
        var input = e.target.closest('[data-bv-qty-in]');
        if (!input) return;
        change(input.closest('[data-bv-line]'), parseInt(input.value, 10) || 0);
      });
    }

    /* ---------- cross-sell adds ---------- */

    section.addEventListener('click', function (e) {
      var add = e.target.closest('[data-bv-xadd]');
      if (!add) return;

      if (!window.BVFX || !window.BVFX.addItems) return;

      var label = add.textContent;
      add.disabled = true;
      add.textContent = 'Adding…';

      window.BVFX.addItems([{ id: parseInt(add.dataset.variant, 10), quantity: 1 }]).then(function (res) {
        if (res && res.ok) {
          /* A new line has to be rendered by Liquid — price, variant title,
             discounts and all — so this is the one place a reload is the
             honest answer. */
          window.location.reload();
        } else {
          add.disabled = false;
          add.textContent = label;
          toast({ title: 'Could not add that', sub: res ? res.message : '', error: true });
        }
      });
    });

    /* ---------- order note ---------- */

    var note = section.querySelector('[data-bv-note]');
    var noteState = section.querySelector('[data-bv-note-state]');

    if (note) {
      var timer = 0;
      var save = function () {
        fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note: note.value })
        }).then(function () {
          if (!noteState) return;
          noteState.hidden = false;
          clearTimeout(timer);
          timer = setTimeout(function () { noteState.hidden = true; }, 2200);
        });
      };

      var debounce = 0;
      note.addEventListener('input', function () {
        clearTimeout(debounce);
        debounce = setTimeout(save, 700);
      });
      note.addEventListener('blur', function () {
        clearTimeout(debounce);
        save();
      });
    }

    /* The meter reads BVFX's cart, which may not have loaded when this runs. */
    if (window.BVFX && window.BVFX.refresh) {
      window.BVFX.refresh().then(function () {
        if (window.BVFX.refreshMeter) window.BVFX.refreshMeter(section);
      });
    }

    document.addEventListener('bv:routine:changed', paintCross);
  }

  function boot() {
    [].slice.call(document.querySelectorAll('[data-bv-cart]')).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target.querySelector ? e.target.querySelector('[data-bv-cart]') : null;
    if (s) init(s);
  });
})();
